'use client';

import { forwardRef, type HTMLAttributes } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import { Alert02Icon } from '@terra/ui-icons';
import { MetaDefault } from '../../../../components/typography';
import { TraceExpandChevron } from './TraceExpandChevron';
import { getTraceTextShimmerStyle } from '../utils/trace-text-shimmer';
import {
  TRACE_INLINE_LABEL_COLOR,
  TRACE_INLINE_ROW_GAP,
  TRACE_INLINE_ROW_MIN_HEIGHT,
  TRACE_INLINE_SUMMARY_COLOR,
} from '../utils/trace-inline-layout';

type TraceInlineStepRowProps = HTMLAttributes<HTMLDivElement> & {
  label: string;
  summary?: string;
  errorSummary?: string;
  durationLabel?: string;
  isError?: boolean;
  canExpand: boolean;
  isExpanded: boolean;
  shimmer?: boolean;
  onHideMessage?: () => void;
};

export const TraceInlineStepRow = forwardRef<
  HTMLDivElement,
  TraceInlineStepRowProps
>(function TraceInlineStepRow(
  {
    label,
    summary,
    errorSummary,
    durationLabel,
    isError = false,
    canExpand,
    isExpanded,
    shimmer = false,
    onHideMessage,
    ...rest
  },
  ref,
) {
  const collapsedSummary = isExpanded ? undefined : (errorSummary ?? summary);

  return (
    <HStack
      ref={ref}
      data-trace-inline-row=""
      data-error={isError ? '' : undefined}
      gap={TRACE_INLINE_ROW_GAP}
      alignItems="center"
      minW={0}
      flex={1}
      minHeight={TRACE_INLINE_ROW_MIN_HEIGHT}
      cursor={canExpand ? 'pointer' : 'default'}
      color={TRACE_INLINE_LABEL_COLOR}
      _hover={canExpand ? { color: 'fg.default' } : undefined}
      transition="color 0.15s ease"
      {...rest}
    >
      <MetaDefault
        color="inherit"
        flexShrink={0}
        whiteSpace="nowrap"
        animationStyle={shimmer ? 'shimmer' : undefined}
        style={getTraceTextShimmerStyle(shimmer)}
      >
        {label}
      </MetaDefault>
      {isError ? (
        <Box display="flex" flexShrink={0} color="fg.error">
          <Alert02Icon size={12} />
        </Box>
      ) : null}
      {collapsedSummary ? (
        <MetaDefault
          color={errorSummary ? 'fg.error' : TRACE_INLINE_SUMMARY_COLOR}
          minW={0}
          flexShrink={1}
          truncate
          title={collapsedSummary}
        >
          {collapsedSummary}
        </MetaDefault>
      ) : null}
      {durationLabel ? (
        <MetaDefault color="fg.subtle" flexShrink={0} whiteSpace="nowrap">
          {durationLabel}
        </MetaDefault>
      ) : null}
      {canExpand ? (
        <TraceExpandChevron
          isExpanded={isExpanded}
          onHideMessage={onHideMessage}
        />
      ) : null}
    </HStack>
  );
});
